import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("nene_cart") || "[]").map((i) => ({ ...i, key: i.key || i.id, variant: i.variant || "" }));
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem("nene_cart", JSON.stringify(items));
    } catch {}
  }, [items]);

  const add = (product, qty = 1, variant = "") => {
    const stock = Number(product.stock) || 0;
    if (stock <= 0) {
      toast.error("Ce produit est épuisé");
      return;
    }
    const key = variant ? `${product.id}::${variant}` : product.id;
    const label = variant ? `${product.name} (${variant})` : product.name;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        if (existing.qty >= stock) {
          toast.info(`Stock maximum atteint pour « ${label} »`);
          return prev;
        }
        return prev.map((i) =>
          i.key === key ? { ...i, qty: Math.min(i.qty + qty, stock) } : i
        );
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          variant,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.images?.[0] || "",
          qty: Math.min(qty, stock),
          stock,
        },
      ];
    });
    toast.success(`« ${label} » ajouté au panier`);
    setOpen(true);
  };

  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const setQty = (key, qty) =>
    setItems((prev) =>
      prev.map((i) =>
        i.key === key ? { ...i, qty: Math.max(1, Math.min(qty, i.stock)) } : i
      )
    );

  const clear = () => setItems([]);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.qty * i.price, 0),
    [items]
  );

  return (
    <CartCtx.Provider
      value={{ items, add, remove, setQty, clear, count, subtotal, open, setOpen }}
    >
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
